import { type ReactNode } from 'react';
import { useDisabledPageRedirect } from '../../../lib/useDisabledPageRedirect';
import { Link } from '../../../components/Link';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import type { AddonTier, BillingMode } from '../../../lib/types';
import { useFeatures, featureLabel, type FeatureKind } from '../../../lib/useFeatures';
import { useAddonTiers } from '../../../lib/useAddonTiers';
import { useBillingMode } from '../../../lib/useBillingMode';
import { SITE_ORIGIN, Seo, breadcrumbSchema, faqSchema, featurePageSchema } from '../../../lib/seo';
import { useAuth } from '../../../lib/auth';
import { CreditCostTable } from '../../../components/CreditCostTable';
import { PublicFooter } from '../../../components/PublicFooter';
import { LearnCrossLink } from '../../../components/LearnCrossLink';
import { PublicHeader } from '../../../components/PublicHeader';
import { Button, cn } from '../../../components/ui';
import { Icon } from '../../../components/icons';

/**
 * Per-feature accent palette. Each colour drives the hero gradient + section
 * accents + button/highlight tones so the five feature pages share one shell but
 * read as distinct surfaces. Keys match the tokens already in our Tailwind theme
 * so we don't introduce one-off CSS variables.
 */
export type FeatureAccent = 'allow' | 'verify' | 'review' | 'accent';

/**
 * Map a marketing addonKind ('blacklist' / 'inbox_placement' / …) to the credit-
 * cost key the wallet uses ('blacklist' / 'inboxPlacement' / …). Lets the layout
 * pull the per-action cost out of `useBillingMode().creditCosts` without each
 * page re-deriving the mapping. Returns null for kinds without a wallet weight
 * (warmup is per-mailbox, not a credit draw).
 */
const ADDON_TO_CREDIT_FEATURE: Record<
  NonNullable<FeatureContent['addonKind']>,
  keyof BillingMode['creditCosts'] | null
> = {
  blacklist: 'blacklist',
  inbox_placement: 'inboxPlacement',
  phone_validation: 'phoneValidation',
  warmup_inbox: null, // per-mailbox billing, not wallet-metered
};

const ACCENT_TONE: Record<FeatureAccent, {
  textClass: string;
  border: string;
  glow: string;
  heroFrom: string;
  badge: string;
}> = {
  allow: {
    textClass: 'text-allow',
    border: 'border-allow/30',
    glow: 'shadow-[0_24px_60px_rgba(34,170,90,0.18)]',
    heroFrom: 'from-allow/10',
    badge: 'bg-allow/10 text-allow',
  },
  verify: {
    textClass: 'text-verify',
    border: 'border-verify/30',
    glow: 'shadow-[0_24px_60px_rgba(238,170,30,0.20)]',
    heroFrom: 'from-verify/10',
    badge: 'bg-verify/10 text-verify',
  },
  review: {
    textClass: 'text-review',
    border: 'border-review/30',
    glow: 'shadow-[0_24px_60px_rgba(230,80,80,0.18)]',
    heroFrom: 'from-review/10',
    badge: 'bg-review/10 text-review',
  },
  accent: {
    textClass: 'text-accent',
    border: 'border-accentdeep/40',
    glow: 'shadow-[0_24px_60px_rgba(62,200,255,0.18)]',
    heroFrom: 'from-accentdeep/10',
    badge: 'bg-accentdeep/10 text-accent',
  },
};

/**
 * Content schema each feature page renders against. Keeps the layout component
 * trivially testable + lets every feature add or remove sections without forking
 * the shell. `relatedFeatures` is the cross-link rail at the bottom — a tenant on
 * a feature page often wants to compare with the adjacent ones.
 */
export interface FeatureContent {
  /**
   * Feature-flag kind for toggleable add-ons. The layout uses this to redirect
   * to / when the super-admin has globally disabled the feature. Omit for CORE
   * product features that are always available (Bot Detection, Email
   * Verification) — they don't appear in the per-feature toggle map.
   */
  kind?: FeatureKind;
  /** URL slug under /features/<slug>. Also used for SEO canonical url. */
  slug: string;
  title: string;
  tagline: string;
  /**
   * SEO meta description + Product-schema description (≤160 chars for the SERP snippet). Falls
   * back to `tagline` when omitted, but `tagline` is a terse hero subtitle (~60 chars) that
   * under-fills the ~155-char snippet Google renders — set this on commercial pages to claim the
   * full width with keyword-rich copy. See the SEO/CWV audit (docs/SEO-CWV-AUDIT-2026-07.md).
   */
  metaDescription?: string;
  /** Eyebrow above the H1 — short identifier like "Add-on" or "Plan-bundled". */
  eyebrow: string;
  /** One-paragraph hero description, sits below the H1. */
  description: string;
  icon: ReactNode;
  accent: FeatureAccent;
  /** Optional AI-generated hero backdrop (public/heroes/*.webp), tuned to this feature's accent.
   *  Applied as a screen-blended CSS background behind the hero — decorative, never the LCP. */
  heroBg?: string;
  /** "What it does" section — high-level value propositions. */
  pillars: Array<{ title: string; body: string }>;
  /** "How it works" — step-by-step / lifecycle. Rendered as a static numbered list unless
   *  `story` is provided (the scroll-driven walk-through), in which case `story` replaces it. */
  steps: Array<{ title: string; body: string }>;
  /**
   * Optional scroll-driven "how it works" story (a <ScrollStory>-based component) that replaces
   * the static `steps` list — the per-feature equivalent of the homepage walk-through. Provide it
   * to give a feature the animated A-to-Z of what happens under the hood.
   */
  story?: ReactNode;
  /**
   * "What's included" — checklist surfaced in the Trial / Pro / add-on column.
   * Each bucket may optionally override the default `heading` / `subtext` when
   * the layout's regime-aware defaults would be misleading for this feature
   * (e.g. Warmup isn't accessible on the platform trial, so its trial column
   * shouldn't say "Free preview — no card required").
   */
  included: Array<{
    scope: 'trial' | 'pro' | 'addon';
    items: string[];
    heading?: string;
    subtext?: string;
  }>;
  /**
   * Sales FAQ — usually 3-5 entries that pre-empt common objections. The `a`
   * side accepts either a plain string OR a function of `billingMode`, so
   * copy that depends on a super-admin flag (e.g. `chargeUpgradesImmediately`)
   * can resolve to the correct wording without maintaining two hand-forked
   * versions of the page.
   */
  faq: Array<{ q: string; a: string | ((mode: BillingMode) => string) }>;
  /** Which add-on slug to pull tier prices for, if any. Omit for plan-bundled features. */
  addonKind?: 'blacklist' | 'inbox_placement' | 'phone_validation' | 'warmup_inbox';
  /**
   * Which credit-cost key to render in the wallet-mode pricing card. Set on every
   * credit-metered feature including core ones that don't have an `addonKind`
   * (detection / emailVerification / domainHealth). When both `addonKind` and
   * `creditFeature` are set, wallet mode uses `creditFeature` and legacy mode
   * uses `addonKind`. When only one is set, the other regime renders nothing
   * (which is the right behaviour for a core feature without a slider product).
   *
   * Passing an array (e.g. `['detection', 'emailVerification']`) sums the
   * weights and renders an "always coupled" note under the total — this is
   * what Bot Anomaly Detection + Email Verification uses because the two
   * halves are always billed together on every /v1/emails call.
   */
  creditFeature?:
    | keyof BillingMode['creditCosts']
    | ReadonlyArray<keyof BillingMode['creditCosts']>;
  /** Cross-link rail at the bottom — typically the 2-3 adjacent features. */
  relatedFeatures: Array<{ slug: string; title: string; tagline: string }>;
  /** Optional in-app screenshot / illustration sidecar above the fold. */
  heroSidecar?: ReactNode;
}

export function FeaturePageLayout({ content }: { content: FeatureContent }) {
  const { feature, isLoading: featuresLoading } = useFeatures();
  const { forAddon } = useAddonTiers('public');
  const user = useAuth((s) => s.user);
  const billingMode = useBillingMode();

  // Page-level SEO meta + structured data. Three JSON-LD payloads stacked:
  //
  //   - Product → name + description + URL show up on the SERP card.
  //   - FAQPage → Google can show the Q&A directly inline (huge CTR boost).
  //   - BreadcrumbList → "Home › Features › <feature>" trail in the SERP.
  const origin = SITE_ORIGIN;
  // Resolve function-form answers to strings before feeding to render + SEO
  // schema. Function-form answers use `billingMode` to switch between the two
  // cash-protection regimes (chargeUpgradesImmediately on/off).
  const resolvedFaq = content.faq.map((f) => ({
    q: f.q,
    a: typeof f.a === 'function' ? f.a(billingMode) : f.a,
  }));

  // Redirect when the super-admin has globally disabled the feature (warmup also counts as
  // disabled when no tier is priced — folded into the flag server-side). Keeps the URL surface
  // clean: no thin/orphan pages reachable when the feature is off. Core features (no `kind` —
  // Bot Detection, Email Verification, Domain Health) are always available; the gate only fires
  // when `kind` is set AND disabled.
  //
  // After mount, not during render: flags are seeded into the SSG snapshot now, so a render-time
  // <Navigate> would emit a redirect stub instead of the page. See useDisabledPageRedirect.
  const featureDisabled = content.kind ? !featuresLoading && !feature(content.kind) : false;
  useDisabledPageRedirect(featureDisabled);
  // Render nothing once the LIVE flag says the feature is off, so an in-app navigation never
  // flashes the page before the redirect fires (same guard as the About page). Prerender-safe:
  // features default ENABLED at build, so featureDisabled is false there and the full page still
  // prerenders with content + schema for SEO; this only blanks after a live flag flips it off.
  if (featureDisabled) return null;

  const tone = ACCENT_TONE[content.accent];
  const tiers = content.addonKind ? forAddon(content.addonKind) : [];

  return (
    <>
      <Seo
        title={content.title}
        description={content.metaDescription ?? content.tagline}
        path={`/features/${content.slug}`}
        // noindex once we know the feature is disabled — keeps a turned-off feature's page
        // out of the index even before the redirect below fires. `feature()` is optimistic
        // (true) while loading, so enabled pages aren't briefly flagged noindex.
        // Core features (no `kind`) always index — only redirected pages opt out.
        index={content.kind ? feature(content.kind) : true}
        keywords={[content.title, ...content.pillars.map((p) => p.title), 'email deliverability'].join(', ')}
        jsonLd={[
          featurePageSchema({
            name: content.title,
            description: content.metaDescription ?? content.tagline,
            path: `/features/${content.slug}`,
          }),
          faqSchema(resolvedFaq),
          breadcrumbSchema([
            { name: 'Home', url: `${origin}/` },
            { name: 'Features', url: `${origin}/features` },
            { name: content.title, url: `${origin}/features/${content.slug}` },
          ]),
        ]}
      />
    <div data-portal className="mx-auto max-w-[1180px] px-6">
      <PublicHeader />

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      {/* The full-bleed hero backdrop is the largest element, so Chrome makes it the LCP — preload
          it (a few KB webp) so LCP lands near the text paint. React 19 hoists this into <head>;
          rendered per feature page, so each preloads only its own image. */}
      {content.heroBg && (
        <link rel="preload" as="image" href={content.heroBg} fetchPriority="high" />
      )}
      <section
        className={cn(
          'relative mt-10 overflow-hidden rounded-3xl border bg-gradient-to-br to-panel/60 p-10',
          tone.border,
          tone.heroFrom,
          tone.glow,
        )}
      >
        {/* Accent-matched AI backdrops (public/heroes/feat-*.webp). Two theme-specific layers:
            the dark glow-on-black (screen-blended) and the light colour-on-white (multiply-blended,
            named feat-*-light.webp). CSS in index.css cross-fades them on the data-theme flip, so
            there's no JS and no hydration mismatch. The dark layer is the one preloaded above. */}
        {content.heroBg && (
          <>
            <div
              aria-hidden
              className="sg-hero-dark pointer-events-none absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${content.heroBg})` }}
            />
            <div
              aria-hidden
              className="sg-hero-light pointer-events-none absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${content.heroBg.replace(/\.webp$/, '-light.webp')})` }}
            />
          </>
        )}
        <div className="relative grid items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <span
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em]',
                tone.badge,
              )}
            >
              <span className={cn('h-1.5 w-1.5 rounded-full', tone.textClass.replace('text-', 'bg-'))} />
              {content.eyebrow}
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl">
              {content.title}
            </h1>
            <p className={cn('mt-3 text-lg font-medium', tone.textClass)}>{content.tagline}</p>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-muted">
              {content.description}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to={user ? '/app' : '/register'}>
                <Button variant="primary" className="h-12 px-5">
                  {user ? 'Open workspace' : 'Start free workspace'}{' '}
                  <Icon.Play width={15} height={15} />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="ghost" className="h-12 px-5">
                  See pricing →
                </Button>
              </Link>
            </div>
          </div>
          {content.heroSidecar && <div className="lg:pl-4">{content.heroSidecar}</div>}
          {!content.heroSidecar && (
            <div className="hidden lg:flex lg:justify-end">
              <div
                className={cn(
                  'flex h-32 w-32 items-center justify-center rounded-3xl border bg-panel2',
                  tone.border,
                  tone.textClass,
                )}
              >
                <div className="scale-[2.5]">{content.icon}</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Value pillars ────────────────────────────────────────────── */}
      <section className="mt-20">
        <Eyebrow tone={tone}>What you get</Eyebrow>
        <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight">
          Built around what tenants actually need to ship deliverability.
        </h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {content.pillars.map((p) => (
            <div
              key={p.title}
              className={cn('rounded-2xl border bg-panel p-6 transition hover:bg-panel2', tone.border)}
            >
              <h3 className={cn('text-base font-semibold', tone.textClass)}>{p.title}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────── */}
      {/* The scroll-driven `story` (when supplied) replaces the static numbered steps — it carries
          its own heading/eyebrow, so we don't wrap it in the section header below. */}
      {content.story ? (
        content.story
      ) : (
        <section className="mt-20">
          <Eyebrow tone={tone}>How it works</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight">
            Plug it in, then forget it. Results land in the dashboard.
          </h2>
          <ol className="mt-8 grid gap-4 md:grid-cols-2">
            {content.steps.map((s, i) => (
              <li key={s.title} className="rounded-2xl border border-bordersoft bg-panel p-6">
                <div className="flex items-start gap-3">
                  <span
                    className={cn(
                      'flex h-7 w-7 flex-none items-center justify-center rounded-full font-mono text-[12px] font-semibold',
                      tone.badge,
                    )}
                  >
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="text-base font-semibold">{s.title}</h3>
                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted">{s.body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* ── What's included by tier ──────────────────────────────────── */}
      {/* The informational counterpart to this commercial page — see LearnCrossLink. Renders
          nothing for features whose articles have not shipped yet. */}
      <LearnCrossLink path={`/features/${content.slug}`} />

      {content.included.length > 0 && (
        <section className="mt-20">
          <Eyebrow tone={tone}>What's included</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight">
            {billingMode.unifiedWalletEnabled
              ? "What lands in your workspace by plan + credit wallet."
              : 'What lands in your workspace by plan + add-on.'}
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {content.included.map((bucket) => (
              <IncludedColumn
                key={bucket.scope}
                bucket={bucket}
                tone={tone}
                walletEnabled={billingMode.unifiedWalletEnabled}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Pricing section: addon tier strip (legacy) OR wallet cost (Phase 4+) ──
          Render whenever either an addon ladder OR a credit-feature is set; the
          orchestrator picks the right widget for the active billing regime. */}
      {(content.addonKind || content.creditFeature) && (
        <PricingSection
          addonKind={content.addonKind}
          creditFeature={content.creditFeature}
          tiers={tiers}
          tone={tone}
          billingMode={billingMode}
        />
      )}

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      {resolvedFaq.length > 0 && (
        <section className="mt-20">
          <Eyebrow tone={tone}>FAQ</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight">
            Quick answers.
          </h2>
          <div className="mt-8 flex flex-col gap-2">
            {resolvedFaq.map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl border border-bordersoft bg-panel p-5 transition open:bg-panel2"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-3 text-[14.5px] font-semibold">
                  {f.q}
                  <span className={cn('font-mono text-[12px] transition group-open:rotate-45', tone.textClass)}>
                    +
                  </span>
                </summary>
                <p className="mt-3 text-[13.5px] leading-relaxed text-muted">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* ── Bottom CTA ───────────────────────────────────────────────── */}
      <section
        className={cn(
          'mt-20 overflow-hidden rounded-3xl border bg-gradient-to-r to-panel/80 p-10 text-center',
          tone.border,
          tone.heroFrom,
        )}
      >
        <h2 className="font-display text-3xl font-bold tracking-tight">
          Try {content.title} during your free trial.
        </h2>
        <p className="mx-auto mt-3 max-w-md text-[14px] text-muted">
          Spin up a workspace, plug in your data, see results — no card required to start.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link to={user ? '/app' : '/register'}>
            <Button variant="primary" className="h-12 px-5">
              {user ? 'Open workspace' : 'Create workspace'} <Icon.Play width={15} height={15} />
            </Button>
          </Link>
          <Link to="/pricing">
            <Button variant="ghost" className="h-12 px-5">
              Compare all plans
            </Button>
          </Link>
        </div>
      </section>

      {/* ── Related features ─────────────────────────────────────────── */}
      {content.relatedFeatures.length > 0 && (
        <section className="mt-20">
          <Eyebrow tone={tone}>Pair well together</Eyebrow>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {content.relatedFeatures.map((r) => (
              <Link
                key={r.slug}
                to={`/features/${r.slug}`}
                className="rounded-2xl border border-bordersoft bg-panel p-5 transition hover:border-accentdeep hover:bg-panel2"
              >
                <div className="text-base font-semibold">{r.title} →</div>
                <p className="mt-1.5 text-[13px] text-muted">{r.tagline}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <PublicFooter />
    </div>
    </>
  );
}

function Eyebrow({
  tone,
  children,
}: {
  tone: (typeof ACCENT_TONE)[FeatureAccent];
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em]',
        tone.badge,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', tone.textClass.replace('text-', 'bg-'))} />
      {children}
    </span>
  );
}

function IncludedColumn({
  bucket,
  tone,
  walletEnabled,
}: {
  bucket: { scope: 'trial' | 'pro' | 'addon'; items: string[]; heading?: string; subtext?: string };
  tone: (typeof ACCENT_TONE)[FeatureAccent];
  walletEnabled: boolean;
}) {
  // Same trial + Pro columns in both modes — those concepts are unchanged. The
  // "addon" column is the one that changes meaning under the wallet: it's no
  // longer a separate slider you tune, it's the credit wallet itself spilling
  // into credit packs when the monthly allowance is empty. Per-bucket overrides
  // win — Warmup uses this to correct the trial column's "Free preview" default
  // since Warmup isn't actually accessible on the platform trial.
  const heading =
    bucket.heading ??
    (bucket.scope === 'trial'
      ? 'During Trial'
      : bucket.scope === 'pro'
        ? 'Pro plan (bundled)'
        : walletEnabled
          ? 'Credit wallet + packs'
          : 'Add-on slider');
  const subtext =
    bucket.subtext ??
    (bucket.scope === 'trial'
      ? 'Free preview — no card required'
      : bucket.scope === 'pro'
        ? 'Built into the price'
        : walletEnabled
          ? "Spent from the monthly wallet — top up with credit packs"
          : 'Stripe-pro-rated, slide on top');
  return (
    <div className="rounded-2xl border border-bordersoft bg-panel p-6">
      <div className={cn('font-mono text-[10.5px] uppercase tracking-[0.14em]', tone.textClass)}>
        {heading}
      </div>
      <div className="mt-1 text-[12px] text-faint">{subtext}</div>
      <ul className="mt-4 flex flex-col gap-2 text-[13.5px] text-muted">
        {bucket.items.map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className={cn('mt-0.5', tone.textClass)}>✓</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Pricing section orchestrator. Two render branches, exclusive:
 *
 *   - Legacy (wallet OFF): the original add-on slider TierStrip — quota / month
 *     + €/mo + ≈ €/unit for each tier. Lets a prospect shop the per-feature
 *     slider before signing up.
 *
 *   - Wallet (wallet ON):  the per-feature line of the credit-cost table
 *     (e.g. "Inbox placement test — 150 credits — ≈ €0.45 per test" for
 *     ref price 3000c/1000cr). Reinforces that the feature is paid out of
 *     the unified pool, points back to /pricing#how-credits-work and to the
 *     credit packs page for overflow.
 *
 * Warmup is the odd case — addonKind exists but ADDON_TO_CREDIT_FEATURE returns
 * null because warmup is per-mailbox billed, not credit-metered. We fall back to
 * the legacy TierStrip in that case regardless of the wallet flag.
 */
function PricingSection({
  addonKind,
  creditFeature,
  tiers,
  tone,
  billingMode,
}: {
  addonKind?: FeatureContent['addonKind'];
  creditFeature?: FeatureContent['creditFeature'];
  tiers: ReadonlyArray<AddonTier>;
  tone: (typeof ACCENT_TONE)[FeatureAccent];
  billingMode: BillingMode;
}) {
  // Wallet ON: prefer the explicit `creditFeature` set by the page, falling back
  // to the addonKind → creditFeature map for pages that only declare addonKind.
  // Returns null when neither resolves (e.g. warmup, which is per-mailbox billed).
  if (billingMode.unifiedWalletEnabled) {
    const fallback = addonKind ? ADDON_TO_CREDIT_FEATURE[addonKind] : null;
    const creditKeys: ReadonlyArray<keyof BillingMode['creditCosts']> = Array.isArray(
      creditFeature,
    )
      ? creditFeature
      : creditFeature
        ? [creditFeature]
        : fallback
          ? [fallback]
          : [];
    if (creditKeys.length > 0) {
      return <WalletPricingCard creditKeys={creditKeys} billingMode={billingMode} tone={tone} />;
    }
    // No wallet weight (warmup) — fall through to the legacy tier strip below.
  }
  // Legacy regime (or fallback): render the slider tier strip. Pages without an
  // addonKind (core features like Detection / Email Verification / DH) render
  // nothing in the legacy regime because there's no per-feature slider to shop.
  if (!addonKind) return null;
  const liveTiers = tiers.filter((t) => t.quota > 0);
  if (liveTiers.length === 0) return null;
  return (
    <section className="mt-16">
      <Eyebrow tone={tone}>Add-on tiers</Eyebrow>
      <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight">
        Top up from a slider — Stripe-pro-rated every move.
      </h2>
      <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-muted">
        Live prices — every change we make to add-on tiers shows up here
        immediately. No "request a quote" friction.
      </p>
      <TierStrip tiers={liveTiers} tone={tone} />
    </section>
  );
}

/**
 * Wallet-mode pricing card. Renders the per-action cost for THIS feature pulled
 * from `billingMode.creditCosts`, plus the reference-price preview, plus the
 * one-line "what one credit buys" framing. Deliberately compact — full anatomy
 * + cross-feature comparison lives on /pricing#how-credits-work.
 */
function WalletPricingCard({
  creditKeys,
  billingMode,
  tone,
}: {
  creditKeys: ReadonlyArray<keyof BillingMode['creditCosts']>;
  billingMode: BillingMode;
  tone: (typeof ACCENT_TONE)[FeatureAccent];
}) {
  // Sum every key the page declared. Single-key pages (Domain Health, Phone
  // Validation, …) just get that one weight; the merged Bot Detection + Email
  // Verification page passes both keys so the total lines up with the coupled
  // "signup screening" row in the CreditCostTable on the right.
  const credits = creditKeys.reduce((sum, k) => sum + (billingMode.creditCosts[k] ?? 0), 0);
  const coupled = creditKeys.length > 1;
  // Deliberately no per-action "≈ €X" hint here — the euro rate depends on the
  // plan / credit pack the tenant buys, and the super-admin reference-price
  // knob would mislead prospects into thinking there's a single platform rate.
  // Send them to the pricing page for exact plan rates instead.

  return (
    <section className="mt-16">
      <Eyebrow tone={tone}>Pricing</Eyebrow>
      <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight">
        Paid from your SieveGuard-Credits wallet.
      </h2>
      <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-muted">
        Every billable action draws from one credit pool — no per-feature slider to
        manage. The cost for this feature is fixed by the platform's credit table; mix
        and match against the other features however you like.
      </p>

      <div
        className={cn(
          'mt-6 grid gap-6 rounded-2xl border bg-panel p-6 lg:grid-cols-[1fr_1fr]',
          tone.border,
        )}
      >
        <div>
          <div className={cn('font-mono text-[10.5px] uppercase tracking-[0.14em]', tone.textClass)}>
            One action costs
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className="font-display text-4xl font-bold text-txt">
              {credits.toLocaleString()}
            </div>
            <div className="text-[14px] text-muted">credits</div>
          </div>
          {coupled && (
            <div className={cn('mt-1 font-mono text-[11px] uppercase tracking-[0.14em]', tone.textClass)}>
              always coupled — one /v1/emails call
            </div>
          )}
          <p className="mt-4 text-[13.5px] leading-relaxed text-muted">
            Pro plans grant 30,000–300,000 credits per month — pick a tier on{' '}
            <Link to="/pricing" className={cn('font-semibold hover:underline', tone.textClass)}>
              the pricing page
            </Link>
            , or top up mid-month with a{' '}
            <Link to="/pricing#addons" className={cn('font-semibold hover:underline', tone.textClass)}>
              credit pack
            </Link>
            .
          </p>
        </div>

        <div className="rounded-xl border border-bordersoft bg-panel2 p-4">
          <div className="mb-3 text-[11px] font-mono uppercase tracking-[0.14em] text-faint">
            Full per-action cost table
          </div>
          <CreditCostTable
            costs={billingMode.creditCosts}
            density="compact"
          />
        </div>
      </div>
    </section>
  );
}

function TierStrip({
  tiers,
  tone,
}: {
  tiers: ReadonlyArray<AddonTier>;
  tone: (typeof ACCENT_TONE)[FeatureAccent];
}) {
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {tiers.map((t) => {
        const perUnit = t.quota > 0 ? t.monthlyCents / 100 / t.quota : 0;
        return (
          <div
            key={t.quota}
            className={cn('rounded-2xl border bg-panel p-5', tone.border)}
          >
            <div className="text-[13px] font-semibold text-txt">{t.label}</div>
            <div className="mt-1 font-mono text-[11px] text-faint">
              {t.quota.toLocaleString()} / month
            </div>
            <div className="mt-3 font-display text-2xl font-bold">
              €{(t.monthlyCents / 100).toFixed(2)}
              <span className="text-[12px] font-normal text-faint"> /mo</span>
            </div>
            {perUnit > 0 && (
              <div className="mt-1 font-mono text-[10.5px] text-faint">
                ≈ €{perUnit.toFixed(3)} / unit
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Tiny helper for prefetching `/plans/addons` — exported so feature pages can wire
 * it on mount without each page re-implementing the same query. Currently unused
 * because the layout calls `useAddonTiers` directly; left here for future SSR /
 * loader integration.
 */
export function usePrefetchAddonTiers() {
  useQuery({
    queryKey: ['public-addons'],
    queryFn: () => api('/plans/addons', { auth: false }),
    staleTime: 5 * 60_000,
  });
}

/** Re-export so feature page modules don't have to import both modules. */
export { featureLabel };

// Default export required by React Router framework-mode route modules.
export default FeaturePageLayout;
